import React, { useState, useMemo, useEffect } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronRight,
  Filter,
  Search,
  BarChart3,
  X,
  Activity,
  ArrowUpDown,
} from "lucide-react";
import Button from "../../../utils/Button/button";
import { Card, CardContent, CardTitle } from "../../../utils/Card/Card";
import Badge from "../../../utils/Badge/Badge";
import { Input } from "../../../utils/Input/Input";
import actions from "../../../redux/indicore/actions";
import { useDispatch, useSelector } from "react-redux";

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../utils/Select/Select";

import "./TransactionList.scss";

export function TransactionsList({ allTransactions, onViewChange }) {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    status: "",
    amountRange: "",
    dateRange: "",
    sortBy: "date-newest",
  });
 const profileData = useSelector(state => state.authReducer.profileData);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  const removeFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
  };
  const clearAllFilters = () => {
    setFilters({
      status: "",
      amountRange: "",
      dateRange: "",
      sortBy: "date-newest",
    });
    setSearchTerm("");
  };

  const filteredTransactions = useMemo(() => {
    let data = allTransactions?.filter((t) => {
      if (
        filters.status &&
        t?.response?.status?.toLowerCase() !== filters.status.toLowerCase()
      ) {
        return false;
      }

      if (filters.amountRange) {
        const amt = Number(t.request.amount);
        if (filters.amountRange === "low" && amt >= 1000) return false;
        if (
          filters.amountRange === "medium" &&
          (amt < 1000 || amt >= 3000)
        )
          return false;
        if (filters.amountRange === "high" && amt < 3000) return false;
      }

      if (filters.dateRange) {
        const txnDate = new Date(t.createdAt);
        const now = new Date();
        const diffDays = Math.floor(
          (now.getTime() - txnDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (filters.dateRange === "today" && diffDays !== 0) return false;
        if (filters.dateRange === "week" && diffDays > 7) return false;
        if (filters.dateRange === "15days" && diffDays > 15) return false;
        if (filters.dateRange === "month" && diffDays > 30) return false;
      }

      if (searchTerm) {
        const ref = t.refId?.toLowerCase() || "";
        if (!ref.includes(searchTerm.toLowerCase())) return false;
      }

      return true;
    });

    data.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      switch (filters.sortBy) {
        case "date-newest":
          return dateB - dateA;
        case "date-oldest":
          return dateA - dateB;
        case "amount-high":
          return Number(b.request.amount) - Number(a.request.amount);
        case "amount-low":
          return Number(a.request.amount) - Number(b.request.amount);
        default:
          return 0;
      }
    });

    return data;
  }, [filters, allTransactions, searchTerm]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(start, start + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  useEffect(() => {
    dispatch(actions.getAllTransaction(profileData?.orgId));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  // Helper to show current sort label in the header
  const sortLabel = () => {
    switch (filters.sortBy) {
      case "date-oldest":
        return "Date (Oldest)";
      case "amount-high":
        return "Amount (High-Low)";
      case "amount-low":
        return "Amount (Low-High)";
      default:
        return "Date (Newest)";
    }
  };

  return (
    <div className="transactions-page">
      <div className="page-container">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <Button
              className="back-btn"
              onClick={() => onViewChange("dashboard")}
            >
              <ArrowLeft className="icon-small" />
              Back to Dashboard
            </Button>
            <div className="title">
              <div className="title-icon">
                <BarChart3 className="icon" />
              </div>
              <h1 className="title-text">All Transactions</h1>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <Search className="search-icon" />
          <Input
            placeholder="Search by Ref ID..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filters */}
        <Card className="filter-card">
          <CardContent className="filter-row">
            <div className="filter-label">
              <Filter className="filter-icon" />
              <span>Filters:</span>
            </div>

            {/* Status filter */}
            <div className="filter-item">
              <Select onValueChange={(val) => updateFilter("status", val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Success">Success</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failure">Failure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Amount Range filter */}
            <div>
              <Select onValueChange={(val) => updateFilter("amountRange", val)}>
                <SelectTrigger className="w-[150px] bg-white/50 border-white/30">
                  <SelectValue placeholder="Amount Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low (₹0-₹999)</SelectItem>
                  <SelectItem value="medium">Medium (₹1000-₹2999)</SelectItem>
                  <SelectItem value="high">High (₹3000+)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range filter */}
            <div>
              <Select onValueChange={(val) => updateFilter("dateRange", val)}>
                <SelectTrigger className="w-[130px] bg-white/50 border-white/30">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="15days">Last 15 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort By filter */}
            <div>
              <Select
                value={filters.sortBy}
                onValueChange={(val) => updateFilter("sortBy", val)}
              >
                <SelectTrigger className="w-[140px] bg-white/50 border-white/30">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-newest">Date (Newest)</SelectItem>
                  <SelectItem value="date-oldest">Date (Oldest)</SelectItem>
                  <SelectItem value="amount-high">Amount (High-Low)</SelectItem>
                  <SelectItem value="amount-low">Amount (Low-High)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>

          {/* Active Filters – sortBy removed */}
          {(filters.status ||
            filters.amountRange ||
            filters.dateRange ||
            searchTerm) && (
            <div className="active-filters">
              <span className="label">Active Filters:</span>

              {searchTerm && (
                <div className="filter-badge">
                  Search: {searchTerm}
                  <Button
                    className="remove-btn"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="icon" />
                  </Button>
                </div>
              )}

              {filters.status && (
                <div className="filter-badge">
                  Status: {filters.status}
                  <Button
                    className="remove-btn"
                    onClick={() => removeFilter("status")}
                  >
                    <X className="icon" />
                  </Button>
                </div>
              )}

              {filters.amountRange && (
                <div className="filter-badge">
                  Amount: {filters.amountRange}
                  <Button
                    className="remove-btn"
                    onClick={() => removeFilter("amountRange")}
                  >
                    <X className="icon" />
                  </Button>
                </div>
              )}

              {filters.dateRange && (
                <div className="filter-badge">
                  Date: {filters.dateRange}
                  <Button
                    className="remove-btn"
                    onClick={() => removeFilter("dateRange")}
                  >
                    <X className="icon" />
                  </Button>
                </div>
              )}

              <Button className="clear-all-btn" onClick={clearAllFilters}>
                Clear All
                <X className="icon" />
              </Button>
            </div>
          )}
        </Card>

        {/* Transactions List */}
        <Card className="transactions-card">
          <div className="transactions-header">
            <CardTitle className="history-title">
              <div className="history-left">
                <Activity className="icon-medium" />
                Transaction History ({filteredTransactions.length} shown /{" "}
                {allTransactions.length} total)
              </div>
              {/* Just display current sort text */}
              <div className="history-right">
                <ArrowUpDown className="icon-smalll" />
                <span className="selected-sort">{sortLabel()}</span>
              </div>
            </CardTitle>
          </div>

          <CardContent className="transactions-content">
            {paginatedTransactions.map((transaction) => (
              <div
                key={transaction._id}
                className="transaction-card"
                // onClick={() =>
                //   onViewChange("transaction-detail", transaction)
                // }
              >
                <div className="transaction-left">
                  <div
                    className={`transaction-icon ${transaction?.response?.status}`}
                  >
                    {transaction?.response?.status === "Success" ? (
                      <ArrowUpRight className="icon-white" />
                    ) : (
                      <ArrowDownLeft className="icon-white" />
                    )}
                  </div>
                  <div className="transaction-info">
                    <p className="transaction-title">
                      {transaction.request.operator}
                    </p>
                    {new Date(transaction.createdAt).toLocaleString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                    <p style={{ fontSize: "12px", color: "gray" }}>
                      Refid: {transaction.refId}
                    </p>
                  </div>
                </div>

                <div className="transaction-right">
                  <div className="transaction-amount">
                    <p className={`amount ${transaction?.response?.status}`}>
                      ₹{transaction.request.amount}
                    </p>
                    {transaction?.response?.status === "Success" && (
                      <p className="commission"></p>
                    )}
                  </div>
                  <Badge
                    className={`status-badge ${transaction?.response?.status}`}
                  >
                    {transaction?.response?.status}
                  </Badge>
                  <ChevronRight className="chevron" />
                </div>
              </div>
            ))}
          </CardContent>

          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹ Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`page-btn ${page === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next ›
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
